import React, { useContext, useEffect, useState } from 'react';
import {
  Expense,
  ExpenseCategory,
} from '../../../../interfaces/delivery-company/expenses';
import { emptyRecurringExpense } from './utils';
import { useSnackBar } from '../../../../contexts/snackbar';
import axios, { AxiosResponse, isAxiosError } from 'axios';
import {
  Typography,
  LinearProgress,
  Alert,
  FormControl,
  TextField,
  Button,
  Box,
  IconButton,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import { isMobile, isTablet } from 'react-device-detect';
import { CompactMerchantType } from '../../../../interfaces/delivery-company/merchants';
import AutocompleteWithThrottle from '../../../common/autocomplete-with-throttle/autocomplete-with-throttle';
import ConfirmAction from '../../../common/confirm-action/confirm-action';
import { ErrorsFlattenner } from '../../../common/errors-flattenner/errors-flattenner';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import MoneyInput from '../../../common/money-input/money-input';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { Backspace } from '@mui/icons-material';
import CustomDialog from '../../../common/custom-dialog/custom-dialog';
import { format } from 'date-fns';
import {
  RecurrentExpense,
  RecurrentExpenseFrequency,
} from '../../../../interfaces/delivery-company/recurrent-expenses';
import Grid2 from '@mui/material/Unstable_Grid2';
import { DataResources, ResourceActionType, user_has_data_model_access } from '../../../../utils/permissions';

interface RecurrentExpenseFormProps {
  defaultRecurrentExpense?: RecurrentExpense;
  onSuccess: (e: RecurrentExpense) => void;
}
function RecurrentExpenseForm({
  defaultRecurrentExpense,
  onSuccess,
}: RecurrentExpenseFormProps) {
  const [recurrentExpenseData, setRecurrentExpenseData] =
    useState<RecurrentExpense>(
      defaultRecurrentExpense || emptyRecurringExpense
    );
  const { showMessage } = useSnackBar();
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [response, setResponse] = useState<AxiosResponse>();
  const { user, config } = useContext(AuthContext);
  const [creatingNewLabel, setCreatingNewLabel] = useState(false);
  const [savingLabel, setSavingLabel] = useState(false);
  const [labelInput, setLabelInput] = useState<string>('');

  const [startDate, setStartDate] = useState<Dayjs | null>(
    defaultRecurrentExpense?.start_date
      ? dayjs(new Date(defaultRecurrentExpense.start_date))
      : null
  );

  const [endDate, setEndDate] = useState<Dayjs | null>(
    defaultRecurrentExpense?.end_date
      ? dayjs(new Date(defaultRecurrentExpense.end_date))
      : null
  );

  const handleSaveNewLabel = async () => {
    setSavingLabel(true);
    try {
      const response = await axios.post<ExpenseCategory>(
        `${API_ENDPOINT}/v1/expense-categories/`,
        { label: labelInput },
        config
      );
      setLabelInput('');
      setRecurrentExpenseData({
        ...recurrentExpenseData,
        category: response.data,
      });
      showMessage('Label created successfully');
      setCreatingNewLabel(false);
    } catch (e) {
      showMessage('Failed to create label. Please try again.', 'error');
    } finally {
      setSavingLabel(false);
    }
  };
  useEffect(() => {
    setRecurrentExpenseData({
      ...recurrentExpenseData,
      start_date: startDate
        ? format(startDate.toDate(), 'yyyy-MM-dd')
        : undefined,
      end_date: endDate ? format(endDate.toDate(), 'yyyy-MM-dd') : undefined,
    });
  }, [startDate, endDate]);

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      setMessage('');
      setResponse(undefined);
      let response;
      if (defaultRecurrentExpense == undefined) {
        response = await axios.post<RecurrentExpense>(
          `${API_ENDPOINT}/v1/recurrent-payments/`,
          recurrentExpenseData,
          config
        );
      } else {
        response = await axios.put<RecurrentExpense>(
          `${API_ENDPOINT}/v1/recurrent-payments/${defaultRecurrentExpense.id}/`,
          recurrentExpenseData,
          config
        );
      }

      showMessage('Recurrent Payment Saved Successfully');
      onSuccess(response.data);
    } catch (e) {
      showMessage('Failed to save recurrent payment.', 'error');
      if (isAxiosError(e)) {
        setResponse(e.response);
        setMessage(
          'Failed to save recurrent payment' +
            (e.response?.statusText ? e.response.statusText : '')
        );
      } else {
        setMessage('Failed to save recurrent payment');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFetchLabels = async () => {
    try {
      return (
        await axios.get<{ count: number; results: ExpenseCategory[] }>(
          `${API_ENDPOINT}/v1/expense-categories/`,
          config
        )
      ).data.results;
    } catch (e) {
      showMessage('Failed to load labels', 'error');
    }
    return [];
  };

  return (
    <Box>
      <Typography
        variant="h4"
        component="h4"
        sx={{ textAlign: 'center', mt: 3, mb: 3 }}
      >
        {recurrentExpenseData.id == 0
          ? 'Create Recurrent Payment'
          : 'Edit Recurrent Payment'}
      </Typography>
      {isLoading ? (
        <>
          Saving Expense... <br /> <LinearProgress />
        </>
      ) : (
        <>
          {message != '' && (
            <>
              <Alert
                severity={
                  response && response.status >= 200 && response.status < 300
                    ? 'success'
                    : 'error'
                }
              >
                <>
                  {message}

                  {response && response.status >= 300 && (
                    <ErrorsFlattenner rawErrors={response.data} />
                  )}
                </>
              </Alert>
              <br />{' '}
            </>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <FormControl fullWidth>
              <TextField
                required
                value={recurrentExpenseData.name}
                onChange={(e) =>
                  setRecurrentExpenseData({
                    ...recurrentExpenseData,
                    name: e.target.value,
                  })
                }
                autoComplete='off'
                label={'Unique Payment Name'}
              />
            </FormControl>
            <br />
            <br />

            <Grid2 container spacing={2}>
              <Grid2 xs={12} md={6}>
                <AutocompleteWithThrottle
                  label="Expense Category"
                  fetch={handleFetchLabels}
                  fetchAllOnLoad
                  disabled={isLoading}
                  onClickAdd={user_has_data_model_access(user, DataResources.EXPENSE_CATEGORIES, ResourceActionType.CREATE) ? (() => setCreatingNewLabel(true)) : undefined}
                  value={
                    recurrentExpenseData.category != null
                      ? recurrentExpenseData.category
                      : null
                  }
                  getOptionLabel={(option: ExpenseCategory) => option.label}
                  onChange={(e, value) => {
                    const expenseLabel = value;
                    if (expenseLabel != null)
                      setRecurrentExpenseData({
                        ...recurrentExpenseData,
                        category: expenseLabel,
                      });
                    else {
                      setRecurrentExpenseData({
                        ...recurrentExpenseData,
                        category: undefined,
                      });
                    }
                  }}
                />
              </Grid2>
              <Grid2 xs={12} md={6}>
                <FormControl key={'user-type-input'} fullWidth>
                  <InputLabel id="user-type-select-label"> Schedule</InputLabel>
                  <Select
                    fullWidth
                    required
                    value={recurrentExpenseData.frequency}
                    label={'Schedule'}
                    onChange={(e) =>
                      setRecurrentExpenseData({
                        ...recurrentExpenseData,
                        frequency: e.target.value as RecurrentExpenseFrequency,
                      })
                    }
                  >
                    {Array.from(Object.values(RecurrentExpenseFrequency)).map(
                      (item) => (
                        <MenuItem key={item} value={item}>
                          {item}
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              </Grid2>
            </Grid2>

            <br />

            <MoneyInput
              money={recurrentExpenseData.expense}
              disabled={isLoading}
              setMoney={(m) =>
                setRecurrentExpenseData({ ...recurrentExpenseData, expense: m })
              }
              label={'Expense Amount'}
            />

            <br />

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  marginBottom: 4,
                }}
              >
                <FormControl fullWidth>
                  <DatePicker
                    disabled={isLoading}
                    label="Start Date"
                    value={startDate}
                   onChange={(newValue) => {
                      if (dayjs(newValue).isValid()) {
                        setStartDate(dayjs(newValue));
                      } else {
                        setStartDate(null);
                      }
                    }}
                  />
                </FormControl>
                <IconButton onClick={() => setStartDate(null)}>
                  <Backspace color={'primary'} />
                </IconButton>
              </div>

              <br />

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  marginBottom: 4,
                }}
              >
                <FormControl fullWidth>
                  <DatePicker
                    disabled={isLoading}
                    label="End Date"
                    value={endDate}
                    onChange={(newValue) => {
                      if (dayjs(newValue).isValid()) {
                        setEndDate(dayjs(newValue));
                      } else {
                        setEndDate(null);
                      }
                    }}
                  />
                </FormControl>
                <IconButton onClick={() => setEndDate(null)}>
                  <Backspace color={'primary'} />
                </IconButton>
              </div>
            </LocalizationProvider>
            <br />
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                size={(isMobile && !isTablet) ? 'small' : 'medium'}
                type="submit"
                variant="contained"
                disabled={isLoading || !user_has_data_model_access(user, DataResources.RECURRING_PAYMENTS, ResourceActionType.UPDATE)}
              >
                Save Recurrent Payment{' '}
                {isLoading && <CircularProgress sx={{ ml: 2 }} size={20} />}
              </Button>
            </div>
          </form>{' '}
        </>
      )}

      {creatingNewLabel && (
        <CustomDialog open={creatingNewLabel} onClose={() => {setLabelInput(""); setCreatingNewLabel(false)}}>
          <DialogTitle>New Expense Catgory</DialogTitle>
          <DialogContent>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSaveNewLabel();
              }}
            >
              <br />
              <FormControl fullWidth>
                <TextField
                  required
                  label="Category Label"
                  placeholder="Enter category label"
                  value={labelInput}
                  onChange={(e) => setLabelInput(e.target.value)}
                  disabled={savingLabel}
                ></TextField>
              </FormControl>

              <br />
              <br />
              <div style={{ width: '100%', textAlign: 'center' }}>
                <Button
                  disabled={savingLabel}
                  variant="contained"
                  type="submit"
                >
                  Save{' '}
                  {savingLabel && <CircularProgress sx={{ ml: 2 }} size={20} />}
                </Button>
              </div>
            </form>
          </DialogContent>
        </CustomDialog>
      )}
    </Box>
  );
}

export default RecurrentExpenseForm;
